import { useState } from "react";
import { Link } from "react-router";
import { ImageOff } from "lucide-react";
import { motion } from "motion/react";
import type { NavigationOrigin } from "@/features/boutique/hooks/useNavigationOrigin";
import { NoiseCard } from "@/features/shop";
import type { AutoScrollSlideProps } from "@/features/shop/types/autoScrollSlider.types";
import { H4 } from "@/shared/ui/typographies/h4";
import { P } from "@/shared/ui/typographies/paragraph";

const MAX_CLICK_MOVE_PX = 5;
const MAX_CLICK_DURATION_MS = 500;
// Keyboard activation (Enter) fires a click with detail 0 and no pointerdown.
const KEYBOARD_CLICK_DETAIL = 0;

const HOMEPAGE_NAVIGATION_ORIGIN: NavigationOrigin = {
  type: "homepage",
  path: "/boutique",
  label: "Accueil",
};

const EXTERNAL_LINK_ATTRIBUTES = { target: "_blank", rel: "noopener noreferrer" };

// CMS links are free text typed by admins: a malformed one must not break the card.
const isExternalUrl = (link: string) =>
  URL.canParse(link, window.location.origin)
  && new URL(link, window.location.origin).origin !== window.location.origin;

const addOpacityToColor = (color: string | undefined, opacity: number): string => {
  if (!color) return `rgba(31, 41, 55, ${opacity})`;

  if (color.startsWith("#")) {
    const hex = color.replace("#", "");
    const hexLength = hex.length;

    if (hexLength === 8) {
      return `#${hex.substring(0, 6)}${Math.round(opacity * 255).toString(16).padStart(2, "0")}`;
    }

    if (hexLength === 6) {
      return `#${hex}${Math.round(opacity * 255).toString(16).padStart(2, "0")}`;
    }

    const r = Number.parseInt(hex.substring(0, 2), 16);
    const g = Number.parseInt(hex.substring(2, 4), 16);
    const b = Number.parseInt(hex.substring(4, 6), 16);
    return `rgba(${r}, ${g}, ${b}, ${opacity})`;
  }

  if (color.startsWith("rgb")) {
    const rgbaMatch = color.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)(?:,\s*[\d.]+)?\)/);
    if (rgbaMatch) {
      return `rgba(${rgbaMatch[1]}, ${rgbaMatch[2]}, ${rgbaMatch[3]}, ${opacity})`;
    }
  }

  return color;
};

export const AutoScrollSlide = ({
  title,
  subtitle,
  backgroundColor,
  image,
  imageBackground,
  link,
  isDragging,
  isClone = false,
}: AutoScrollSlideProps) => {
  const [isHovering, setIsHovering] = useState(false);
  const pointerDownRef = useState({ x: 0, y: 0, time: 0 })[0];

  const handlePointerDown = (e: React.PointerEvent) => {
    pointerDownRef.x = e.clientX;
    pointerDownRef.y = e.clientY;
    pointerDownRef.time = Date.now();
  };

  // The slider is draggable: a click ending a drag or a long press must not open the link.
  const isDeliberateClick = (e: React.MouseEvent) => {
    if (pointerDownRef.time === 0) return false;

    const hasMoved = Math.abs(e.clientX - pointerDownRef.x) > MAX_CLICK_MOVE_PX
      || Math.abs(e.clientY - pointerDownRef.y) > MAX_CLICK_MOVE_PX;
    const isLongPress = Date.now() - pointerDownRef.time > MAX_CLICK_DURATION_MS;

    return !hasMoved && !isLongPress;
  };

  const handleLinkClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    if (e.detail === KEYBOARD_CLICK_DETAIL) return;

    if (!isDeliberateClick(e)) {
      e.preventDefault();
      return;
    }

    pointerDownRef.time = 0;
  };

  const card = (
    <NoiseCard
      containerClassName="w-[350px] h-[160px]"
      containerStyle={{ backgroundColor: backgroundColor || "#6b7280" }}
      className="!p-4"
    >
      <div className="flex h-full gap-4">
        <div className="flex-1 flex flex-col justify-center">
          <H4 className="text-white mb-1 line-clamp-2">
            {title}
          </H4>
          <P className="text-white/80 text-sm line-clamp-3">
            {subtitle}
          </P>
        </div>

        <div className="flex-shrink-0 flex items-center justify-center">
          <motion.div
            className="rounded-2xl p-3 shadow-lg overflow-hidden"
            style={{
              backgroundColor: addOpacityToColor(imageBackground, 0.3),
              transform: isHovering
                ? "perspective(1000px) rotateY(5deg) rotateX(-5deg)"
                : "perspective(1000px) rotateY(0deg) rotateX(0deg)",
              transition: "transform 0.3s ease-out",
            }}
          >
            {image
              ? (
                <img
                  src={image}
                  alt=""
                  className="w-24 h-24 object-cover object-center rounded-xl pointer-events-none select-none"
                  style={{
                    imageRendering: "auto",
                    WebkitBackfaceVisibility: "hidden",
                    backfaceVisibility: "hidden",
                    transform: "translateZ(0)",
                    willChange: "transform",
                    WebkitFontSmoothing: "antialiased",
                    MozOsxFontSmoothing: "grayscale",
                  }}
                  loading="lazy"
                  decoding="async"
                  draggable="false"
                />
              )
              : (
                <div className="w-24 h-24 flex items-center justify-center">
                  <ImageOff className="w-12 h-12 text-gray-400" />
                </div>
              )}
          </motion.div>
        </div>
      </div>
    </NoiseCard>
  );

  return (
    <motion.div
      onMouseEnter={() => setIsHovering(true)}
      onMouseLeave={() => setIsHovering(false)}
      onPointerDown={handlePointerDown}
      // The slider repeats its cards to loop: only the first copy is announced and tabbable.
      aria-hidden={isClone || undefined}
      whileHover={{ scale: 1.05 }}
      transition={{ duration: 0.3 }}
      className={`relative ${isDragging ? "cursor-grabbing" : "cursor-pointer"}`}
    >
      {link
        ? (
          <Link
            to={link}
            state={{ navigationOrigin: HOMEPAGE_NAVIGATION_ORIGIN }}
            {...(isExternalUrl(link) && EXTERNAL_LINK_ATTRIBUTES)}
            onClick={handleLinkClick}
            tabIndex={isClone ? -1 : undefined}
            draggable={false}
            className="block rounded-2xl focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white"
          >
            {card}
          </Link>
        )
        : card}
    </motion.div>
  );
};
